import { DndContext, type DragEndEvent, type Modifier, MouseSensor, TouchSensor, useSensor } from '@dnd-kit/core';
import { restrictToParentElement, restrictToVerticalAxis, restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  SortableContext,
  type SortingStrategy,
  rectSortingStrategy,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Grid } from '@mui/material';
import React, { useMemo } from 'react';

type SortableItemProps = { id: string; children: (isDragging: boolean) => React.ReactNode; gridItem?: boolean };

export const SortableItem = (props: SortableItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    // if is dragging, show visual feedback
    ...(isDragging
      ? {
          zIndex: 500,
          opacity: 0.75,
          // grow the item when dragging
          transform: CSS.Transform.toString({
            ...transform!,
            scaleX: 1.05,
            scaleY: 1.05,
          }),
        }
      : {}),
  } as React.CSSProperties;

  const GridWrapper = ({ children }: { children: React.ReactNode }) => (
    <Grid key={props.id} size={{ xs: 12, sm: 4, md: 3, lg: 3, xl: 2 }}>
      {children}
    </Grid>
  );

  const Wrapper = useMemo(() => {
    if (props?.gridItem) return GridWrapper;
    return React.Fragment;
  }, [props?.gridItem]);

  return (
    <Wrapper>
      <div ref={setNodeRef} style={style} {...attributes} {...listeners} className={isDragging ? 'is-dragging' : ''}>
        {props?.children?.(isDragging)}
      </div>
    </Wrapper>
  );
};

type SortableListItems = {
  id: string;
  [key: string]: unknown;
};

type SortableListProps<T extends SortableListItems> = {
  onDragEnd: (event: DragEndEvent) => void;
  items: T[];
  children?: (item: T, isDragging: boolean, index: number) => React.ReactNode;
  disabled?: boolean;
  grid?: boolean;
};

export const SortableList = <T extends SortableListItems>(props: SortableListProps<T>) => {
  const listRef = React.useRef<HTMLDivElement>(null);

  const handleDragEnd = (event: DragEndEvent) => {
    props.onDragEnd(event);
  };

  const mouseSensor = useSensor(MouseSensor, {
    // Require the mouse be held before activating
    activationConstraint: {
      delay: 400,
      tolerance: 5,
    },
  });

  const touchSensor = useSensor(TouchSensor, {
    // Require to be held before activating
    activationConstraint: {
      delay: 400,
      tolerance: 5,
    },
  });

  const GridWrapper = ({ children }: { children: React.ReactNode }) => (
    <Grid container spacing={2} width={'100%'}>
      {children}
    </Grid>
  );

  const Wrapper = useMemo(() => {
    if (props?.grid) return GridWrapper;
    return React.Fragment;
  }, [props?.grid]);

  const params: { modifiers: Modifier[]; strategy: SortingStrategy } = useMemo(() => {
    if (props?.grid)
      return {
        modifiers: [restrictToWindowEdges],
        strategy: rectSortingStrategy,
      };

    return {
      modifiers: [restrictToVerticalAxis, restrictToParentElement],
      strategy: verticalListSortingStrategy,
    };
  }, [props?.grid]);

  // If grid, center the content
  const flexStyle = props?.grid
    ? {
        display: 'flex',
        justifyContent: 'center',
      }
    : {};

  return (
    <div style={flexStyle} ref={listRef}>
      <DndContext
        autoScroll
        onDragEnd={handleDragEnd}
        modifiers={params.modifiers}
        sensors={[mouseSensor, touchSensor]}
      >
        <Wrapper>
          <SortableContext items={props.items} strategy={params.strategy} disabled={props?.disabled}>
            {props?.items?.map((item, index) => (
              <SortableItem id={item?.id} key={item?.id} gridItem={props?.grid}>
                {(isDragging) => props?.children?.(item, isDragging, index)}
              </SortableItem>
            ))}
          </SortableContext>
        </Wrapper>
      </DndContext>
    </div>
  );
};
